import { Link, useLocation, Outlet } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard,
  FolderKanban,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Plus,
  Users,
  Crown,
  Building2,
  Home,
  Menu,
} from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription, SUBSCRIPTION_TIERS } from "@/contexts/SubscriptionContext";
import { Logo } from "@/components/ui/logo";
import { useIsMobile } from "@/hooks/use-mobile";

const sidebarLinks = [
  { icon: LayoutDashboard, label: "Dashboard", href: "/dashboard" },
  { icon: FolderKanban, label: "Projects", href: "/dashboard/projects" },
  { icon: Users, label: "Personas", href: "/personas" },
  { icon: Building2, label: "Organization", href: "/organization" },
  { icon: Settings, label: "Settings", href: "/settings" },
];

function SidebarContent({
  collapsed,
  location,
  tier,
  tierInfo,
  userInitial,
  userEmail,
  signOut,
  setCollapsed,
  onNavigate,
}: {
  collapsed: boolean;
  location: ReturnType<typeof useLocation>;
  tier: string;
  tierInfo: { name: string };
  userInitial: string;
  userEmail: string;
  signOut: () => void;
  setCollapsed?: (v: boolean) => void;
  onNavigate?: () => void;
}) {
  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-border">
        {collapsed && setCollapsed ? (
          <button
            onClick={() => setCollapsed(false)}
            className="group relative flex items-center justify-center h-8 w-8 flex-shrink-0 rounded-md transition-colors hover:bg-muted/50"
            aria-label="Expand sidebar"
          >
            <Logo variant="square" className="h-8 w-8 transition-opacity group-hover:opacity-0" />
            <ChevronRight className="w-5 h-5 text-muted-foreground absolute opacity-0 transition-opacity group-hover:opacity-100" />
          </button>
        ) : (
          <>
            <Link to="/dashboard" className="flex items-center gap-2" onClick={onNavigate}>
              <Logo variant="full" className="h-8 w-auto flex-shrink-0" />
            </Link>
            {setCollapsed && (
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCollapsed(true)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
            )}
          </>
        )}
      </div>

      {/* New Run Button */}
      <div className="p-4">
        <Button
          asChild
          className={cn(
            "w-full gradient-primary text-primary-foreground hover:opacity-90 transition-opacity",
            collapsed && "px-0",
          )}
        >
          <Link to="/dashboard/new-run" onClick={onNavigate}>
            <Plus className="w-4 h-4" />
            {!collapsed && <span className="ml-2">New Run</span>}
          </Link>
        </Button>
      </div>

      {/* Nav Links */}
      <nav className="flex-1 px-3 py-2">
        <ul className="space-y-1">
          {sidebarLinks.map((link) => {
            const isActive =
              location.pathname === link.href ||
              (link.href !== "/dashboard" && location.pathname.startsWith(link.href));

            return (
              <li key={link.href}>
                <Link
                  to={link.href}
                  onClick={onNavigate}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
                  )}
                >
                  <link.icon className="w-5 h-5 flex-shrink-0" />
                  {!collapsed && <span>{link.label}</span>}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Theme & Home */}
      <div className="px-4 pb-2 flex items-center gap-2">
        <ThemeToggle />
        {!collapsed ? (
          <Button asChild variant="ghost" size="sm" className="flex-1 justify-start text-muted-foreground hover:text-foreground">
            <Link to="/home" onClick={onNavigate}>
              <Home className="w-4 h-4 mr-2" />
              Home
            </Link>
          </Button>
        ) : (
          <Button asChild variant="ghost" size="icon" className="h-9 w-9">
            <Link to="/home" onClick={onNavigate}><Home className="w-4 h-4" /></Link>
          </Button>
        )}
      </div>

      {/* Plan Badge */}
      {!collapsed && tier !== "free" && (
        <div className="px-4 pb-2">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10">
            <Crown className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">{tierInfo.name} Plan</span>
          </div>
        </div>
      )}

      {/* User Section */}
      <div className="p-4 border-t border-border">
        <div className={cn("flex items-center gap-3", collapsed && "justify-center")}>
          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-medium text-primary">{userInitial}</span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{userEmail.split("@")[0]}</p>
              <p className="text-xs text-muted-foreground truncate">{userEmail}</p>
            </div>
          )}
          {!collapsed && (
            <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={signOut}>
              <LogOut className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function AppLayout() {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(true); // default collapsed on desktop
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { tier } = useSubscription();

  const tierInfo = SUBSCRIPTION_TIERS[tier];
  const userInitial = user?.email?.[0]?.toUpperCase() || "U";
  const userEmail = user?.email || "user@example.com";

  const sharedProps = {
    location,
    tier,
    tierInfo,
    userInitial,
    userEmail,
    signOut,
  };

  if (isMobile) {
    return (
      <div className="min-h-screen flex flex-col w-full bg-background">
        {/* Mobile top bar */}
        <header className="h-14 flex items-center justify-between px-4 border-b border-border bg-card sticky top-0 z-40">
          <Link to="/dashboard">
            <Logo variant="full" className="h-7 w-auto" />
          </Link>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] p-0 bg-card border-border">
              <SidebarContent
                collapsed={false}
                {...sharedProps}
                onNavigate={() => setMobileOpen(false)}
              />
            </SheetContent>
          </Sheet>
        </header>

        <main className="flex-1">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex w-full bg-background">
      {/* Desktop Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 h-full border-r border-border bg-card z-40 transition-all duration-300 hidden md:block",
          collapsed ? "w-16" : "w-64",
        )}
      >
        <SidebarContent
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          {...sharedProps}
        />
      </aside>

      {/* Main Content */}
      <main className={cn("flex-1 transition-all duration-300", collapsed ? "md:ml-16" : "md:ml-64")}>
        <Outlet />
      </main>
    </div>
  );
}
